import type { CompareColumn, CompareRequest } from "@/lib/api/compare";
import { DEFAULT_RANKING_OPTIONS } from "@/lib/api/compare";

import {
  DEFAULT_COMPARE_CHANNEL_ID,
  normalizeCompareChannelId,
} from "./channels.js";

export const MAX_COMPARE_VERSIONS = 4;
export const DEFAULT_COMPARE_VERSION = "benchmark_20260604";

export const DEFAULT_COMPARE_REQUEST: CompareRequest = {
  baseUrl: "http://search-kingpinv1-internal.test.commerce.nikecloud.com.cn:8080",
  searchParams: {
    channelId: DEFAULT_COMPARE_CHANNEL_ID,
    language: "zh-Hans",
    marketplace: "CN",
    searchTerms: "篮球鞋",
    anchor: 0,
    count: 50,
    merchRules: false,
    explain: true,
  },
  headers: {
    accept: "application/json",
    nikeApiCallerId: "nike:gcde:northstar",
  },
  versions: [
    {
      version: DEFAULT_COMPARE_VERSION,
      ranking: { ...DEFAULT_RANKING_OPTIONS },
      merchRules: false,
      explain: true,
    },
  ],
};

/** Parsed compare route search (flat query params on /compare). */
export interface CompareSearch {
  baseUrl?: string;
  searchTerms?: string;
  channelId?: string;
  anchor?: string | number;
  count?: string | number;
  merchRules?: string | boolean;
  explain?: string | boolean;
  /** ISO-8601 UTC Kingpin previewDate (e.g. 2026-06-25T04:13:21.648Z). */
  previewDate?: string;
  callerId?: string;
  /** Pipe-separated version ids when columns use defaults only. */
  versions?: string;
  /** JSON-encoded CompareColumn[] when per-column options differ. */
  columns?: string;
  hideMatched?: string | boolean;
  run?: string | boolean;
}

export function normalizeScalar(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "1" : "0";
  if (typeof value !== "string") return undefined;

  let t = value.trim();
  if (t.length === 0) return undefined;

  if (
    (t.startsWith('"') && t.endsWith('"')) ||
    (t.startsWith("'") && t.endsWith("'"))
  ) {
    try {
      const parsed: unknown = JSON.parse(t);
      if (typeof parsed === "string" || typeof parsed === "number") {
        return String(parsed);
      }
    } catch {
      t = t.slice(1, -1).trim();
    }
  }

  return t;
}

function pickString(search: Record<string, unknown>, key: string): string | undefined {
  return normalizeScalar(search[key]);
}

function parseIntParam(raw: string | number | undefined, fallback: number): number {
  const normalized = normalizeScalar(raw);
  if (normalized == null) return fallback;
  const n = Number.parseInt(normalized, 10);
  return Number.isFinite(n) ? n : fallback;
}

function parseBoolParam(raw: string | boolean | undefined, fallback: boolean): boolean {
  if (raw === true) return true;
  if (raw === false) return false;
  const normalized = normalizeScalar(raw);
  if (normalized == null) return fallback;
  return normalized === "1" || normalized === "true";
}

function columnFromVersionId(version: string): CompareColumn {
  const d = DEFAULT_COMPARE_REQUEST.versions[0]!;
  return { ...d, version };
}

export function parseVersionsParam(raw: string | undefined): CompareColumn[] {
  if (raw == null || raw === "") {
    return [...DEFAULT_COMPARE_REQUEST.versions];
  }
  const parts = raw.split("|").map((s) => s.trim());
  const capped = parts.slice(0, MAX_COMPARE_VERSIONS);
  return capped.length > 0
    ? capped.map(columnFromVersionId)
    : [...DEFAULT_COMPARE_REQUEST.versions];
}

function parseColumnsJson(raw: string | undefined): CompareColumn[] | undefined {
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return undefined;
    return (parsed as CompareColumn[]).slice(0, MAX_COMPARE_VERSIONS).map((col) => ({
      version: typeof col.version === "string" ? col.version : "",
      ...(col.ranking ? { ranking: { ...DEFAULT_RANKING_OPTIONS, ...col.ranking } } : {}),
      ...(col.merchRules !== undefined ? { merchRules: Boolean(col.merchRules) } : {}),
      ...(col.explain !== undefined ? { explain: Boolean(col.explain) } : {}),
      ...(col.rollUpField ? { rollUpField: col.rollUpField } : {}),
    }));
  } catch {
    return undefined;
  }
}

function columnsUseDefaults(columns: CompareColumn[]): boolean {
  const d = DEFAULT_COMPARE_REQUEST.versions[0]!;
  return columns.every((col) => {
    const ranking = col.ranking ?? DEFAULT_RANKING_OPTIONS;
    return (
      ranking.rankingServiceFlow === d.ranking!.rankingServiceFlow &&
      ranking.rankingServiceStrategy === d.ranking!.rankingServiceStrategy &&
      ranking.manualBoostMode === d.ranking!.manualBoostMode &&
      ranking.manualBoostSlotsMaxCount === d.ranking!.manualBoostSlotsMaxCount &&
      (col.merchRules ?? d.merchRules) === d.merchRules &&
      (col.explain ?? d.explain) === d.explain &&
      !col.rollUpField
    );
  });
}

export function parseCompareSearch(search: Record<string, unknown>): CompareSearch {
  return {
    baseUrl: pickString(search, "baseUrl"),
    searchTerms: pickString(search, "searchTerms"),
    channelId: pickString(search, "channelId"),
    anchor: pickString(search, "anchor"),
    count: pickString(search, "count"),
    merchRules: pickString(search, "merchRules"),
    explain: pickString(search, "explain"),
    previewDate: pickString(search, "previewDate"),
    callerId: pickString(search, "callerId"),
    versions: pickString(search, "versions"),
    columns: pickString(search, "columns"),
    hideMatched: pickString(search, "hideMatched"),
    run: pickString(search, "run"),
  };
}

export function compareSearchToRequest(search: CompareSearch): CompareRequest {
  const d = DEFAULT_COMPARE_REQUEST;
  const columns =
    parseColumnsJson(search.columns) ?? parseVersionsParam(search.versions);

  return {
    baseUrl: search.baseUrl ?? d.baseUrl,
    searchParams: {
      channelId: normalizeCompareChannelId(search.channelId ?? d.searchParams.channelId),
      language: d.searchParams.language,
      marketplace: d.searchParams.marketplace,
      searchTerms: search.searchTerms ?? d.searchParams.searchTerms,
      anchor: parseIntParam(search.anchor, d.searchParams.anchor),
      count: parseIntParam(search.count, d.searchParams.count),
      merchRules: parseBoolParam(search.merchRules, d.searchParams.merchRules),
      explain: parseBoolParam(search.explain, d.searchParams.explain),
      ...(search.previewDate ? { previewDate: search.previewDate } : {}),
    },
    headers: {
      accept: "application/json",
      nikeApiCallerId: search.callerId ?? d.headers.nikeApiCallerId,
    },
    versions: columns,
  };
}

export function compareRequestToSearch(
  req: CompareRequest,
  extras?: { hideMatched?: boolean; run?: boolean },
): CompareSearch {
  const search: CompareSearch = {
    baseUrl: req.baseUrl,
    searchTerms: req.searchParams.searchTerms,
    channelId: req.searchParams.channelId,
    anchor: req.searchParams.anchor,
    count: req.searchParams.count,
    merchRules: req.searchParams.merchRules,
    explain: req.searchParams.explain,
    ...(req.searchParams.previewDate ? { previewDate: req.searchParams.previewDate } : {}),
  };

  if (columnsUseDefaults(req.versions)) {
    search.versions = req.versions.map((col) => col.version).join("|");
  } else {
    search.columns = JSON.stringify(req.versions);
  }

  const callerId = req.headers.nikeApiCallerId;
  if (callerId) search.callerId = callerId;
  if (extras?.hideMatched) search.hideMatched = true;
  if (extras?.run) search.run = true;
  return search;
}

const DEFAULT_SHARE_SEARCH = compareRequestToSearch(DEFAULT_COMPARE_REQUEST);

export function compareRequestToNavigateSearch(
  req: CompareRequest,
  extras?: { hideMatched?: boolean; run?: boolean },
): CompareSearch {
  const search = compareRequestToSearch(req, extras);
  const out: CompareSearch = {};
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === "") continue;
    if (key === "baseUrl" && value === DEFAULT_SHARE_SEARCH.baseUrl) continue;
    if (key === "callerId" && value === DEFAULT_SHARE_SEARCH.callerId) continue;
    (out as Record<string, string | number | boolean>)[key] = value;
  }
  return out;
}

export function compareSearchKey(search: CompareSearch): string {
  return JSON.stringify(search);
}

export function shouldAutoRunCompare(search: CompareSearch): boolean {
  if (search.run === true) return true;
  const run = normalizeScalar(search.run);
  if (run === "1" || run === "true") return true;
  const terms = normalizeScalar(search.searchTerms);
  const versions = normalizeScalar(search.versions);
  const columns = normalizeScalar(search.columns);
  return Boolean(terms && (versions || columns));
}

export function hideMatchedFromSearch(search: CompareSearch): boolean {
  if (search.hideMatched === true) return true;
  const hide = normalizeScalar(search.hideMatched);
  return hide === "1" || hide === "true";
}
